import { ServiceHandler } from '../../../utils/api.utils';

export interface AsyncTaskUnit {
  index: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  instruction: string;
  reads_notepad: boolean;
  result: string | null;
  error: string | null;
  attempts: number;
}

/** Progress treatment: named steps, or a processed/total units counter. */
export type AsyncTaskView = 'checklist' | 'units';

/** Result treatment: prose shown on the card, or JSON for the data viewer. */
export type AsyncTaskResultView = 'json' | 'text';

/** A single named step in a checklist run. */
export interface AsyncTaskStep {
  readonly label: string;
  readonly status: 'not_started' | 'in_progress' | 'completed' | 'failed';
}

/**
 * The surface-broker request a finished questionnaire run carries.
 *
 * Not a CTA and not a write. There is no button and nothing is POSTed back:
 * the card dispatches `payload` to the broker, which puts the answers onto the
 * open questionnaire as drafts for the user to accept or reject per question.
 *
 * `payload` is forwarded to `dispatchViewSurfaceAction` verbatim — it carries
 * its own `viewId`, `entityId`, `formId` and `changeSetId`, and the broker's
 * stage capability is the only validator. Inner keys are camelCase, unlike the
 * snake_case task fields, because that is the broker's own vocabulary.
 */
export interface AsyncTaskStagePayload {
  readonly payload: Record<string, unknown>;
  /** Display only — the labels the card shows before the broker answers. */
  readonly accountName: string;
  readonly formId: string;
  readonly formName: string;
  readonly fieldCount: number;
  readonly answeredCount: number;
}

/** Downloadable results file produced by artifact-based delivery. */
export interface AsyncTaskDeliveryArtifact {
  readonly artifact_id: string;
  readonly filename: string;
  readonly mime: string;
  readonly size_bytes: number;
  /** Relative chat-agent API path returning { url, filename } to download. */
  readonly presign_path: string;
}

export interface AsyncTaskDetail {
  run_id: string;
  name: string;
  status:
    | 'preparing'
    | 'decomposing'
    | 'running'
    | 'completed'
    | 'failed'
    | 'cancelled';
  total: number;
  processed: number;
  failed_count: number;
  error: string | null;
  created_at: string;
  delivery_result: string | null;
  units: AsyncTaskUnit[];
  shard: string | null;
  shard_count: string | null;
  parent_run_id: string | null;
  /** Task variant (`'extraction'`, `'form'`, …). Identity only — branch on
   *  `view`/`result_view` so a new kind needs no console change. */
  kind?: string;
  /** Optional: older backends omit it, and the card infers it from `kind`. */
  view?: AsyncTaskView;
  /** Optional: older backends omit it, and the card infers it from `kind`. */
  result_view?: AsyncTaskResultView;
  /** Steps for `view: 'checklist'` runs. `[]` until the server publishes them. */
  steps?: AsyncTaskStep[];
  /** Pre-cap harvest size when the unit-cost ceiling truncated the run. */
  capped_from?: number | null;
  /** Results files from artifact delivery. Optional: older backends omit it. */
  delivery_artifacts?: AsyncTaskDeliveryArtifact[] | null;
  /** Server-stamped when a form run finishes with answers worth staging.
   *  Absent on every other kind of run, and on older backends. */
  stage_payload?: AsyncTaskStagePayload | null;
}

export interface AsyncTaskSummary {
  run_id: string;
  name: string;
  status: string;
  processed: number;
  total: number;
  failed_count: number;
  /** See AsyncTaskDetail — the list serves the same hints so an inline card can
   *  pick its treatment without a per-run detail fetch. */
  kind?: string;
  view?: AsyncTaskView;
  result_view?: AsyncTaskResultView;
  steps?: AsyncTaskStep[];
  shard: string | null;
  shard_count: string | null;
  parent_run_id: string | null;
  // Source run this one retries (set by POST :runId/retry-failed). Optional:
  // older backends omit it.
  retried_from?: string | null;
  /** See AsyncTaskDetail. */
  stage_payload?: AsyncTaskStagePayload | null;
}

export interface AsyncTaskListResponse {
  tasks: AsyncTaskSummary[];
}

export const getAsyncTask = ServiceHandler<unknown, AsyncTaskDetail>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/:runId',
});

export const listAsyncTasks = ServiceHandler<unknown, AsyncTaskListResponse>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks',
});

export interface ActiveAsyncTasksResponse {
  active: Array<{
    // Normalized (undashed, lowercase) conversation id.
    conversation_id: string;
    running_count: number;
  }>;
  // Whether the async-tasks feature is live on this deployment. `false` (or
  // absent, on older backends) means there is nothing to poll for. Optional so
  // a backend that predates the flag still type-checks.
  enabled?: boolean;
}

export const getActiveAsyncTasks = ServiceHandler<
  unknown,
  ActiveAsyncTasksResponse
>({
  url: '/api/chat-agent/v1/async-tasks/active',
});

export const cancelAsyncTask = ServiceHandler<
  unknown,
  { run_id: string; status: string }
>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/:runId/cancel',
  method: 'post',
});

// Spawns a NEW run preloaded with this run's failed units (for sharded
// groups, failed units across the WHOLE group). Prep/decompose are skipped;
// the new run appears in the conversation with its own card. 400 {error}
// when the run has no failed units or its state has expired from Redis.
export const retryFailedAsyncTask = ServiceHandler<
  unknown,
  { run_id: string; name: string; unit_count: number }
>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/:runId/retry-failed',
  method: 'post',
});

// HITL guard: start_async_task only stores a proposal; confirming here (with
// the OutputChannel list picked on the card) starts the actual run. Empty
// outputs = results stay in chat only.
export const confirmAsyncTaskProposal = ServiceHandler<
  { outputs: Array<{ type: string; config: Record<string, unknown> }> },
  { run_id: string; status: string; name: string }
>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/proposals/:proposalId/confirm',
  method: 'post',
});

// Lifecycle state for card rehydration: the card's started/cancelled flags
// are component state and vanish on a conversation switch — on remount it
// asks the server what actually happened to the proposal.
export const getAsyncTaskProposalState = ServiceHandler<
  unknown,
  {
    status: 'pending' | 'confirmed' | 'rejected' | 'expired';
    run_id?: string;
    name?: string;
  }
>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/proposals/:proposalId',
});

export const rejectAsyncTaskProposal = ServiceHandler<
  unknown,
  { rejected: boolean }
>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/proposals/:proposalId/reject',
  method: 'post',
});

export interface AsyncTaskUnitDetail {
  index: number;
  status: AsyncTaskUnit['status'];
  instruction: string;
  result: string | null;
  error: string | null;
  attempts: number;
}

/** Full (untruncated) unit detail — the run endpoint truncates unit text to
 *  keep the polling payload small; fetch this when a row is expanded. */
export const getAsyncTaskUnit = ServiceHandler<unknown, AsyncTaskUnitDetail>({
  url: '/api/chat-agent/v1/conversations/:conversationId/async-tasks/:runId/units/:index',
});
